import React, { useRef, useState, useEffect, useCallback } from 'react';
import { EditorContent, useEditor, BubbleMenu } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Highlight from '@tiptap/extension-highlight';
import Typography from '@tiptap/extension-typography';
import Document from '@tiptap/extension-document';
import Text from '@tiptap/extension-text';
import Underline from '@tiptap/extension-underline';
import { Node } from "@tiptap/core";
import { diffLines } from 'diff';
import { useAuth } from '@/contexts/AuthContext';
import './tiptap.css';

interface TipTapEditorProps {
  initialValue?: string;
  onChange?: (content: string) => void;
  onDiffChange?: (diff: any) => void;
  onFocus?: () => void;
  onBlur?: () => void;
  sectionKey: string;
  isListening?: boolean;
}

interface DiffEntry {
  timestamp: string;
  editor: string;
  diff: string;
}

const TipTapEditor: React.FC<TipTapEditorProps> = ({
  initialValue = "",
  onChange,
  onDiffChange,
  onFocus,
  onBlur,
  sectionKey,
  isListening = false
}) => {
  const { user } = useAuth();
  const [content, setContent] = useState(initialValue);
  const [lastSavedContent, setLastSavedContent] = useState(initialValue);
  const [isUserEditing, setIsUserEditing] = useState(false);
  const typingTimerRef = useRef<NodeJS.Timeout | null>(null);
  const TYPING_INTERVAL = 2000;

  // Custom Block Node for special formatting
  const CustomBlock = Node.create({
    name: "customBlock",
    group: "block",
    content: "inline*",
    parseHTML() {
      return [
        {
          tag: "div[data-type='block']",
        },
      ];
    },
    renderHTML({ HTMLAttributes }) {
      return ["div", { ...HTMLAttributes, "data-type": "block" }, 0];
    },
  });

  // Badge Block Node for timestamps
  const BadgeBlock = Node.create({
    name: "badgeBlock",
    inline: true,
    group: "inline",
    content: "text*",
    parseHTML() {
      return [{ tag: "span.badge-block" }];
    },
    renderHTML({ HTMLAttributes }) {
      return ["span", { ...HTMLAttributes, class: "badge-block", contentEditable: "false" }, 0];
    },
  });

  // Function to process content and add classes to bold text with colons
  const processBoldTextWithColons = (htmlContent: string) => {
    return htmlContent.replace(
      /<(strong|b)([^>]*)>([^<]*:)/gi,
      '<$1$2 class="colon-header">$3'
    );
  };

  const editor = useEditor({
    extensions: [
      StarterKit,
      Highlight.configure({
        multicolor: true,
      }),
      Typography,
      Document,
      CustomBlock,
      BadgeBlock,
      Text,
      Underline,
    ],
    editorProps: {
      attributes: {
        class: "prose prose-sm focus:outline-none",
      },
    },
    content: initialValue,
    editable: true, // Always editable
    onUpdate: ({ editor }) => {
      const newContent = editor.getHTML();
      
      // Process content to add line breaks after bold text with colons
      let processedContent = newContent.replace(
        /<(strong|b)([^>]*)>([^<]*:[^<]*)<\/(strong|b)>/gi,
        '<$1$2>$3</$4><br>'
      );

      // Also add colon-header class to bold elements with colons
      processedContent = processBoldTextWithColons(processedContent);
      
      setContent(processedContent);
      
      if (onChange) {
        onChange(processedContent);
      }

      // Solo generar diff si el usuario está editando manualmente
      if (isUserEditing) {
        // Debounce diff calculation
        if (typingTimerRef.current) {
          clearTimeout(typingTimerRef.current);
        }
        
        typingTimerRef.current = setTimeout(() => {
          updateDiff(processedContent);
        }, TYPING_INTERVAL);
      }
    },
    onFocus: () => {
      setIsUserEditing(true);
      if (onFocus) {
        onFocus();
      }
    },
    onBlur: () => {
      // Mantener el estado por un momento para capturar cambios finales
      setTimeout(() => {
        setIsUserEditing(false);
      }, 3000);
      
      if (onBlur) {
        onBlur();
      }
    },
  });

  const updateDiff = useCallback(
    (newContent: string) => {
      if (!user || newContent === lastSavedContent || !isUserEditing) return;

      const diff = diffLines(lastSavedContent, newContent);
      let diffString = "";
      let hasChanges = false;

      diff.forEach((part) => {
        if (part.value && part.value.trim()) {
          const lines = part.value.split("\n").filter((line) => line.trim() !== "");

          lines.forEach((line) => {
            if (part.added) {
              diffString += `<span class="text-green-600">New Correction: ${line.trim()}</span>\n`;
              hasChanges = true;
            } else if (part.removed) {
              diffString += `<span class="text-red-600">Deleted: ${line.trim()}</span>\n`;
              hasChanges = true;
            }
          });
        }
      });

      if (hasChanges && onDiffChange) {
        const newDiff: DiffEntry = {
          timestamp: new Date().toISOString(),
          editor: `${user.first_name || ''} ${user.last_name || ''}`.trim() || user.username || 'Unknown',
          diff: diffString,
        };

        console.log('📝 Generando diff para corrección del usuario:', newDiff);
        onDiffChange(newDiff);
        setLastSavedContent(newContent);
      }
    },
    [lastSavedContent, user, onDiffChange, isUserEditing]
  );

  // Update editor content when initialValue changes (key for fixing analysis switching)
  useEffect(() => {
    if (editor && initialValue !== content) {
      console.log('Actualizando contenido del editor automáticamente (no genera diff):', { initialValue, currentContent: content, sectionKey });
      
      // Marcar que no es edición del usuario
      setIsUserEditing(false);
      
      // Process the initial value to add proper classes
      const processedInitialValue = processBoldTextWithColons(initialValue);
      
      editor.commands.setContent(processedInitialValue, false);
      setContent(processedInitialValue);
      setLastSavedContent(processedInitialValue);
    }
  }, [initialValue, editor, sectionKey]);

  // Handle focus event to potentially stop listening
  const handleFocus = useCallback(() => {
    setIsUserEditing(true);
    if (isListening && editor) {
      console.log('Editor focused, may need to pause transcription');
    }
    if (onFocus) {
      onFocus();
    }
  }, [isListening, editor, onFocus]);

  // Prevent form submission and page reload on button clicks
  const handleButtonClick = useCallback((e: React.MouseEvent, action: () => void) => {
    e.preventDefault();
    e.stopPropagation();
    action();
  }, []);

  // Highlight color functions
  const toggleHighlightRed = useCallback(() => {
    if (editor?.isActive('highlight', { color: '#fecaca' })) {
      editor.chain().focus().unsetHighlight().run();
    } else {
      editor.chain().focus().setHighlight({ color: '#fecaca' }).run();
    }
  }, [editor]);

  const toggleHighlightBlue = useCallback(() => {
    if (editor?.isActive('highlight', { color: '#bfdbfe' })) {
      editor.chain().focus().unsetHighlight().run();
    } else {
      editor.chain().focus().setHighlight({ color: '#bfdbfe' }).run();
    }
  }, [editor]);

  const toggleHighlightGreen = useCallback(() => {
    if (editor?.isActive('highlight', { color: '#bbf7d0' })) {
      editor.chain().focus().unsetHighlight().run();
    } else {
      editor.chain().focus().setHighlight({ color: '#bbf7d0' }).run();
    }
  }, [editor]);

  const toggleHighlightYellow = useCallback(() => {
    if (editor?.isActive('highlight', { color: '#fef08a' })) {
      editor.chain().focus().unsetHighlight().run();
    } else {
      editor.chain().focus().setHighlight({ color: '#fef08a' }).run();
    }
  }, [editor]);

  if (!editor) {
    return (
      <div className="editor-container">
        <div className="animate-pulse bg-gray-200 h-32 rounded"></div>
      </div>
    );
  }

  return (
    <div className="editor-container">
      <BubbleMenu 
        className="bubble-menu" 
        tippyOptions={{ duration: 100 }} 
        editor={editor}
      >
        <button
          type="button"
          onClick={(e) => handleButtonClick(e, () => editor.chain().focus().toggleBold().run())}
          className={editor.isActive('bold') ? 'is-active' : ''}
        >
          Bold
        </button>
        <button
          type="button"
          onClick={(e) => handleButtonClick(e, () => editor.chain().focus().toggleItalic().run())}
          className={editor.isActive('italic') ? 'is-active' : ''}
        >
          Italic
        </button>
        <button
          type="button"
          onClick={(e) => handleButtonClick(e, () => editor.chain().focus().toggleStrike().run())}
          className={editor.isActive('strike') ? 'is-active' : ''}
        >
          Strike
        </button>
        <button
          type="button"
          onClick={(e) => handleButtonClick(e, toggleHighlightRed)}
          className={editor.isActive('highlight', { color: '#fecaca' }) ? 'is-active highlight-red-btn' : 'highlight-red-btn'}
        >
          H
        </button>
        <button
          type="button"
          onClick={(e) => handleButtonClick(e, toggleHighlightBlue)}
          className={editor.isActive('highlight', { color: '#bfdbfe' }) ? 'is-active highlight-blue-btn' : 'highlight-blue-btn'}
        >
          H
        </button>
        <button
          type="button"
          onClick={(e) => handleButtonClick(e, toggleHighlightGreen)}
          className={editor.isActive('highlight', { color: '#bbf7d0' }) ? 'is-active highlight-green-btn' : 'highlight-green-btn'}
        >
          H
        </button>
        <button
          type="button"
          onClick={(e) => handleButtonClick(e, toggleHighlightYellow)}
          className={editor.isActive('highlight', { color: '#fef08a' }) ? 'is-active highlight-yellow-btn' : 'highlight-yellow-btn'}
        >
          H
        </button>
      </BubbleMenu>

      <EditorContent 
        editor={editor}
        onFocus={handleFocus}
      />
    </div>
  );
};

export default TipTapEditor;
